import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';
import { 
  ArrowRight, 
  Brain, 
  Sparkles, 
  Bot,
  Workflow,
  Network,
  Lightbulb,
  CheckCircle
} from 'lucide-react';
import { images } from '../data/mock';

const AIConsultingPage = () => {
  const services = [
    {
      icon: Lightbulb,
      title: "AI Strategy & Integration",
      description: "Develop a comprehensive AI strategy aligned with your business objectives.",
      items: [
        "AI readiness and maturity assessment",
        "Use case identification and prioritization",
        "AI roadmap development",
        "Technology stack selection and planning"
      ]
    },
    {
      icon: Bot,
      title: "Intelligent Automation",
      description: "Automate complex processes with AI-powered solutions.",
      items: [
        "Process automation assessment",
        "Intelligent document processing",
        "Cognitive automation solutions",
        "RPA with AI enhancement"
      ]
    },
    {
      icon: Workflow,
      title: "AI-Enabled Workflows",
      description: "Transform business workflows with embedded intelligence.",
      items: [
        "Workflow analysis and optimization",
        "AI-assisted decision support",
        "Predictive analytics integration",
        "Real-time insights and alerts"
      ]
    },
    {
      icon: Network,
      title: "Embedded AI Systems",
      description: "Integrate AI capabilities directly into your products and services.",
      items: [
        "AI model development and deployment",
        "MLOps and model management",
        "Edge AI solutions",
        "Computer vision and NLP integration"
      ]
    },
    {
      icon: Sparkles,
      title: "AI Agents & Assistants",
      description: "Build intelligent agents that augment human capabilities.",
      items: [
        "Conversational AI and chatbots",
        "Virtual assistants for business processes",
        "AI copilots for productivity",
        "Multi-agent system design"
      ]
    },
    {
      icon: Brain,
      title: "AI Governance & Ethics",
      description: "Ensure responsible AI deployment with proper governance frameworks.",
      items: [
        "AI ethics and bias assessment",
        "Governance framework development",
        "Compliance and regulatory alignment",
        "Explainability and transparency"
      ]
    }
  ];

  return (
    <div className="min-h-screen pt-24">
      <SEO 
        title="AI Consulting Services"
        description="Transform your business with TekClap's AI consulting services. AI strategy, intelligent automation, AI-enabled workflows, and embedded AI systems."
        keywords="AI consulting services, artificial intelligence consulting, intelligent automation, AI strategy, machine learning, AI integration"
      />

      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-[#FFF4E6] to-[#FAFAF8]">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-[#F58B23] font-medium text-sm uppercase tracking-wider mb-4 block">
                AI Consulting
              </span>
              <h1 className="text-4xl md:text-5xl font-serif font-bold text-[#1A1A1A] mb-6">
                AI Consulting Services for Intelligent Business Transformation
              </h1>
              <p className="text-lg text-[#6B7280] leading-relaxed mb-8">
                Unlock the power of artificial intelligence with TekClap's AI consulting services. 
                We help organizations develop AI strategies, implement intelligent automation, 
                and embed AI capabilities into their products and workflows.
              </p>
              <Link to="/contact">
                <Button className="bg-[#F58B23] hover:bg-[#D97706] text-white font-medium px-8 py-6 text-base">
                  Schedule Consultation
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </div>
            <div className="relative">
              <img 
                src={images.aiSolutions} 
                alt="AI Consulting" 
                className="rounded-2xl shadow-2xl w-full h-[400px] object-cover"
              />
              <div className="absolute -bottom-6 -right-6 bg-white p-6 rounded-xl shadow-lg">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-[#FFF4E6] rounded-full flex items-center justify-center">
                    <Brain className="w-6 h-6 text-[#F58B23]" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-[#1A1A1A]">AI-First</div>
                    <div className="text-sm text-[#6B7280]">Approach</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-white">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-[#1A1A1A] mb-6">
              End-to-End AI Consulting Solutions
            </h2>
            <p className="text-lg text-[#6B7280]">
              From strategy to implementation, we guide your AI journey with expertise 
              in the latest technologies and best practices.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div 
                key={index}
                className="bg-[#FAFAF8] rounded-2xl p-8 hover:shadow-lg transition-all duration-300 border border-gray-100"
              >
                <div className="w-14 h-14 bg-[#FFF4E6] rounded-xl flex items-center justify-center mb-6">
                  <service.icon className="w-7 h-7 text-[#F58B23]" />
                </div>
                <h3 className="text-xl font-semibold text-[#1A1A1A] mb-3">
                  {service.title}
                </h3>
                <p className="text-[#6B7280] mb-6">
                  {service.description}
                </p>
                <ul className="space-y-3">
                  {service.items.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3 text-sm text-[#374151]">
                      <CheckCircle className="w-5 h-5 text-[#F58B23] flex-shrink-0 mt-0.5" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#1A1A1A]">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-white mb-6">
            Ready to Harness the Power of AI?
          </h2>
          <p className="text-lg text-gray-300 mb-10 max-w-2xl mx-auto">
            Let&apos;s explore how AI can transform your business operations, enhance decision-making, 
            and create new opportunities for growth.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button className="bg-[#F58B23] hover:bg-[#D97706] text-white font-medium px-8 py-6 text-base">
                Schedule Consultation
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/client-impact">
              <Button variant="outline" className="border-white text-white hover:bg-white hover:text-[#1A1A1A] font-medium px-8 py-6 text-base">
                View Client Impact
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Related Services */}
      <section className="py-20 bg-white">
        <div className="container-custom">
          <h2 className="text-2xl font-serif font-bold text-[#1A1A1A] mb-8">
            Explore Related Services
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            <Link to="/technology-consulting" className="group p-6 bg-[#FAFAF8] rounded-xl hover:shadow-md transition-all">
              <h3 className="font-semibold text-[#1A1A1A] mb-2 group-hover:text-[#F58B23] transition-colors">
                Technology Consulting →
              </h3>
              <p className="text-sm text-[#6B7280]">
                Digital transformation, technology strategy, and engineering services.
              </p>
            </Link>
            <Link to="/product-development" className="group p-6 bg-[#FAFAF8] rounded-xl hover:shadow-md transition-all">
              <h3 className="font-semibold text-[#1A1A1A] mb-2 group-hover:text-[#F58B23] transition-colors">
                Product Development →
              </h3>
              <p className="text-sm text-[#6B7280]">
                AI-powered products, cloud-native development, and custom engineering.
              </p>
            </Link>
            <Link to="/workforce-solutions" className="group p-6 bg-[#FAFAF8] rounded-xl hover:shadow-md transition-all">
              <h3 className="font-semibold text-[#1A1A1A] mb-2 group-hover:text-[#F58B23] transition-colors">
                Workforce Solutions →
              </h3>
              <p className="text-sm text-[#6B7280]">
                AI and data talent, strategic staffing, and team augmentation.
              </p>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AIConsultingPage;
